import React, { useState } from 'react';
import portalApi from '../../../../lib/portalApi';
import { X, Calendar, Clock, Link as LinkIcon, AlertCircle } from 'lucide-react';

export default function CreateCalendarItemModal({
  isOpen,
  onClose,
  onSuccess,
  preselectedStartupId = null,
  assignedStartups = [],
}) {
  const [activeType, setActiveType] = useState('Task'); // 'Task' | 'Deadline' | 'Meeting'
  const [taskScope, setTaskScope] = useState('my_task'); // 'my_task' | 'startup_task'
  const [startupId, setStartupId] = useState(preselectedStartupId || (assignedStartups[0]?.id || ''));
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState('15:00');
  const [durationHours, setDurationHours] = useState('1');
  const [meetingLink, setMeetingLink] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const isPersonalTask = activeType === 'Task' && taskScope === 'my_task';
  const hasNoStartups = assignedStartups.length === 0 && !preselectedStartupId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!isPersonalTask && !startupId) {
      setError('Please select an assigned startup');
      return;
    }

    if (activeType === 'Meeting' && meetingLink && !meetingLink.startsWith('https://')) {
      setError('Meeting link must be an HTTPS URL (e.g. https://meet.google.com/...)');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      if (isPersonalTask) {
        await portalApi.createMentorPersonalTask({
          title: title.trim(),
          description: description.trim(),
          dueDate: date,
          priority,
        });
      } else {
        await portalApi.createMentorCalendarItem({
          type: activeType,
          startupId,
          title: title.trim(),
          description: description.trim(),
          date,
          time: activeType === 'Meeting' ? time : null,
          durationHours: activeType === 'Meeting' ? durationHours : null,
          meetingLink: activeType === 'Meeting' ? meetingLink.trim() : null,
          priority: activeType === 'Task' ? priority : null,
        });
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create item');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
      <div className="bg-[#131318] border border-[#2C2C34] rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
        {/* Header with Type Selector matching PDF Page 6 */}
        <div className="p-5 border-b border-[#2C2C34] flex items-center justify-between">
          <div className="flex items-center gap-1.5 bg-[#08080A] p-1 rounded-xl border border-[#2C2C34]">
            {['Task', 'Deadline', 'Meeting'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setActiveType(t);
                  setError('');
                }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  activeType === t
                    ? 'bg-[#D42B92] text-white shadow-sm'
                    : 'text-[#8E8E99] hover:text-[#F2F2F4]'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#8E8E99] hover:text-[#F2F2F4] hover:bg-white/[0.05]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 text-xs">
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Task Scope: My Personal Task vs Startup Task */}
          {activeType === 'Task' && (
            <div className="space-y-1.5">
              <label className="block text-[#8E8E99] font-medium">Task Scope</label>
              <div className="grid grid-cols-2 gap-2 bg-[#08080A] p-1 rounded-xl border border-[#2C2C34]">
                <button
                  type="button"
                  onClick={() => {
                    setTaskScope('my_task');
                    setError('');
                  }}
                  className={`py-1.5 px-3 rounded-lg text-xs font-semibold transition-all ${
                    taskScope === 'my_task'
                      ? 'bg-[#321129] text-[#F2F2F4] border border-[#D42B92]/40 shadow-sm'
                      : 'text-[#8E8E99] hover:text-[#F2F2F4]'
                  }`}
                >
                  My Personal Task
                </button>
                <button
                  type="button"
                  disabled={hasNoStartups}
                  onClick={() => {
                    setTaskScope('startup_task');
                    setError('');
                  }}
                  className={`py-1.5 px-3 rounded-lg text-xs font-semibold transition-all ${
                    taskScope === 'startup_task'
                      ? 'bg-[#321129] text-[#F2F2F4] border border-[#D42B92]/40 shadow-sm'
                      : 'text-[#8E8E99] hover:text-[#F2F2F4]'
                  } ${hasNoStartups ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  Startup Task {hasNoStartups ? '(No startups)' : ''}
                </button>
              </div>
            </div>
          )}

          {/* Startup Selector (Only shown when item requires a startup) */}
          {!isPersonalTask && (
            <div>
              <label className="block text-[#8E8E99] font-medium mb-1">
                Startup (Assigned only)
              </label>
              {hasNoStartups ? (
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs">
                  No startups currently assigned to you. Personal tasks can still be created without a startup.
                </div>
              ) : preselectedStartupId ? (
                <input
                  type="text"
                  disabled
                  value={assignedStartups.find((s) => s.id === preselectedStartupId)?.name || 'Selected Startup'}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] opacity-80"
                />
              ) : (
                <select
                  value={startupId}
                  onChange={(e) => setStartupId(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                >
                  <option value="">Select an assigned startup...</option>
                  {assignedStartups.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-[#8E8E99] font-medium mb-1">
              {activeType === 'Meeting' ? 'Meeting Title' : activeType === 'Deadline' ? 'Milestone Title' : isPersonalTask ? 'Personal Task Title' : 'Task Title'}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={activeType === 'Meeting' ? 'e.g. Pitch review session' : 'e.g. Revise financial model'}
              required
              className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
            />
          </div>

          {/* Date & Time Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[#8E8E99] font-medium mb-1 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Date
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full px-3 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
              />
            </div>

            {activeType === 'Meeting' ? (
              <div>
                <label className="block text-[#8E8E99] font-medium mb-1 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> Time
                </label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                />
              </div>
            ) : activeType === 'Task' ? (
              <div>
                <label className="block text-[#8E8E99] font-medium mb-1">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Urgent">Urgent</option>
                </select>
              </div>
            ) : null}
          </div>

          {/* Meeting-Specific Fields */}
          {activeType === 'Meeting' && (
            <>
              <div>
                <label className="block text-[#8E8E99] font-medium mb-1 flex items-center gap-1">
                  <LinkIcon className="w-3.5 h-3.5" /> HTTPS Meeting Link (Google Meet / Zoom)
                </label>
                <input
                  type="url"
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  placeholder="https://meet.google.com/abc-defg-hij"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                />
              </div>

              <div>
                <label className="block text-[#8E8E99] font-medium mb-1">Duration</label>
                <select
                  value={durationHours}
                  onChange={(e) => setDurationHours(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
                >
                  <option value="0.5">30 minutes</option>
                  <option value="1">1 hour</option>
                  <option value="1.5">1.5 hours</option>
                  <option value="2">2 hours</option>
                </select>
              </div>
            </>
          )}

          {/* Agenda / Description */}
          <div>
            <label className="block text-[#8E8E99] font-medium mb-1">
              {activeType === 'Meeting' ? 'Agenda / Notes' : 'Description'}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Provide context or instructions..."
              className="w-full px-3.5 py-2 rounded-xl bg-[#08080A] border border-[#2C2C34] text-[#F2F2F4] focus:outline-none focus:border-[#D42B92]"
            />
          </div>

          {/* Footer Submit */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#2C2C34]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-[#2C2C34] text-[#8E8E99] hover:text-[#F2F2F4] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (!isPersonalTask && hasNoStartups)}
              className="px-5 py-2 rounded-xl bg-[#D42B92] hover:bg-[#b02279] text-white font-medium transition-colors disabled:opacity-50"
            >
              {submitting ? 'Saving…' : `Save ${isPersonalTask ? 'Personal Task' : activeType}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
